/**
 * 马赛克图组件 (Mosaic Chart)
 *
 * 使用 D3.js 实现的定制化马赛克图，用于展示体验维度的痛点/爽点分布
 * - 横轴：体验类型（易用性、性能、外观等）
 * - 纵轴：产品品类
 * - 颜色：满意度（红色=痛点<5分，黄色=中性5-7分，绿色=爽点>7分）
 * - 面积：提及次数（方块越大表示被提及越多）
 */

import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { MosaicDataItem, ExperienceType } from '@/types/analysis';
import { useThemeStore } from '@/stores/themeStore';
import './MosaicChart.css';

interface MosaicChartProps {
  data: MosaicDataItem[];
  width?: number;
  height?: number;
  loading?: boolean;
}

// 体验类型中文映射
const EXPERIENCE_LABELS: Record<ExperienceType, string> = {
  usability: '易用性',
  performance: '性能',
  appearance: '外观',
  functionality: '功能完整性',
  reliability: '可靠性',
  support: '客户支持',
};

// 从 CSS 变量读取功能色
const getThemeColors = () => {
  const root = document.documentElement;
  const cs = getComputedStyle(root);
  return {
    error: cs.getPropertyValue('--color-error').trim() || '#FF4D4F',
    warning: cs.getPropertyValue('--color-warning').trim() || '#FAAD14',
    success: cs.getPropertyValue('--color-success').trim() || '#52C41A',
    textPrimary: cs.getPropertyValue('--color-text-primary').trim() || '#1E293B',
    textSecondary: cs.getPropertyValue('--color-text-secondary').trim() || '#5C5C5C',
    textTertiary: cs.getPropertyValue('--color-text-tertiary').trim() || '#8C8C8C',
    primary: cs.getPropertyValue('--color-primary').trim() || '#1677FF',
    bgPrimary: cs.getPropertyValue('--color-bg-primary').trim() || '#FFFFFF',
  };
};

// 满意度颜色映射 - 使用 CSS 变量色
const getSatisfactionColor = (satisfaction: number, colors: ReturnType<typeof getThemeColors>): string => {
  if (satisfaction < 5) return colors.error; // 痛点 - 红色
  if (satisfaction < 7) return colors.warning; // 中性 - 黄色
  return colors.success; // 爽点 - 绿色
};

export function MosaicChart({ data, width = 800, height = 500, loading = false }: MosaicChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const currentTheme = useThemeStore((s) => s.theme);

  useEffect(() => {
    if (!svgRef.current || !data.length || loading) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const colors = getThemeColors();

    // 设置边距
    const margin = { top: 60, right: 120, bottom: 80, left: 100 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // 创建主容器
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // 获取所有唯一的体验类型和产品
    const categories = Array.from(new Set(data.map((d) => d.category)));
    const products = Array.from(new Set(data.map((d) => d.product)));

    // 创建比例尺
    const xScale = d3.scaleBand().domain(categories).range([0, innerWidth]).padding(0.1);

    const yScale = d3.scaleBand().domain(products).range([0, innerHeight]).padding(0.1);

    // 计算每个单元格的面积比例（基于提及次数）
    const maxCount = d3.max(data, (d) => d.count) || 1;
    const sizeScale = d3.scaleSqrt().domain([0, maxCount]).range([0, Math.min(xScale.bandwidth(), yScale.bandwidth())]);

    // 添加标题
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', margin.top / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .style('font-weight', '600')
      .style('fill', colors.textPrimary)
      .text('体验维度痛点/爽点分布（马赛克图）');

    // 绘制方格
    const cells = g
      .selectAll('.mosaic-cell')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'mosaic-cell')
      .attr('transform', (d) => {
        const x = xScale(d.category) || 0;
        const y = yScale(d.product) || 0;
        const cellWidth = xScale.bandwidth();
        const cellHeight = yScale.bandwidth();
        const rectSize = sizeScale(d.count);
        const offsetX = (cellWidth - rectSize) / 2;
        const offsetY = (cellHeight - rectSize) / 2;
        return `translate(${x + offsetX}, ${y + offsetY})`;
      });

    // 添加矩形
    cells
      .append('rect')
      .attr('width', (d) => sizeScale(d.count))
      .attr('height', (d) => sizeScale(d.count))
      .attr('fill', (d) => getSatisfactionColor(d.satisfaction, colors))
      .attr('stroke', colors.bgPrimary)
      .attr('stroke-width', 2)
      .attr('rx', 4)
      .attr('ry', 4)
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this).attr('stroke', colors.primary).attr('stroke-width', 3);

        // 显示 Tooltip
        const tooltip = d3.select('body').append('div').attr('class', 'mosaic-tooltip').style('opacity', 0);

        tooltip
          .html(
            `
            <div>
              <strong>${EXPERIENCE_LABELS[d.category]} - ${d.product}</strong><br/>
              满意度: <strong>${d.satisfaction.toFixed(1)}</strong> 分<br/>
              提及次数: <strong>${d.count}</strong> 次<br/>
              情感: <strong>${d.sentiment === 'positive' ? '正面' : d.sentiment === 'negative' ? '负面' : '中性'}</strong>
            </div>
          `
          )
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY - 28}px`)
          .transition()
          .duration(200)
          .style('opacity', 1);
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke', colors.bgPrimary).attr('stroke-width', 2);
        d3.selectAll('.mosaic-tooltip').remove();
      });

    // 添加 X 轴
    g.append('g')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickFormat((d) => EXPERIENCE_LABELS[d as ExperienceType])
      )
      .selectAll('text')
      .style('text-anchor', 'end')
      .style('font-size', '12px')
      .style('fill', colors.textSecondary)
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    // 添加 Y 轴
    g.append('g')
      .call(d3.axisLeft(yScale))
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', colors.textSecondary);

    // 添加图例
    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width - margin.right + 20}, ${margin.top})`);

    const legendData = [
      { label: '爽点 (>7分)', color: colors.success },
      { label: '中性 (5-7分)', color: colors.warning },
      { label: '痛点 (<5分)', color: colors.error },
    ];

    legend
      .selectAll('.legend-item')
      .data(legendData)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (_d, i) => `translate(0, ${i * 30})`)
      .each(function (d) {
        const item = d3.select(this);
        item.append('rect').attr('width', 16).attr('height', 16).attr('fill', d.color).attr('rx', 2);

        item
          .append('text')
          .attr('x', 24)
          .attr('y', 13)
          .style('font-size', '12px')
          .style('fill', colors.textSecondary)
          .text(d.label);
      });

    // 添加说明文字
    legend
      .append('text')
      .attr('x', 0)
      .attr('y', -15)
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('fill', colors.textPrimary)
      .text('满意度');

    legend
      .append('text')
      .attr('x', 0)
      .attr('y', 110)
      .style('font-size', '11px')
      .style('fill', colors.textTertiary)
      .text('*方块大小表示提及次数');
  }, [data, width, height, loading, currentTheme]);

  if (loading) {
    return (
      <div className="mosaic-chart-loading" style={{ width, height }}>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="mosaic-chart-empty" style={{ width, height }}>
        <div className="empty-message">暂无体验维度数据</div>
        <div className="empty-hint">提示：需要反馈数据包含体验标签才能展示马赛克图</div>
      </div>
    );
  }

  return (
    <div className="mosaic-chart-container">
      <svg ref={svgRef} className="mosaic-chart" />
    </div>
  );
}
